import { useEffect, useMemo, useState } from "react";
import { Trash2, Star, MapPin, Calendar, Camera, Pencil, X, CheckSquare, Square } from "lucide-react";
import { api, jobPhotoSrc, formatApiError } from "../../lib/api";

const GROUPS = [
  { key: "type", label: "Job Type" },
  { key: "location", label: "Location" },
  { key: "date", label: "Job Date" },
];

const PHASES = [
  { value: "", label: "No phase" },
  { value: "before", label: "Before" },
  { value: "during", label: "During" },
  { value: "after", label: "After" },
];

const SERVICE_OPTIONS = [
  "Excavation",
  "Concrete & Foundations",
  "Site Prep & Grading",
  "Decorative & Stamped Concrete",
  "Retaining Walls",
  "Driveways, Paving & Surfacing",
  "Drainage",
];

const keyFn = {
  type: (j) => j.service_type || "Uncategorized",
  location: (j) => j.location || "Unknown location",
  date: (j) => (j.date || "").slice(0, 7) || "Undated",
};

export function PhotosManager({ jobs, onRefresh, isAdmin = true }) {
  const [groupBy, setGroupBy] = useState("type");
  const [selected, setSelected] = useState(null); // job object
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroTarget, setHeroTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [batchMode, setBatchMode] = useState(false);
  const [batchIds, setBatchIds] = useState(new Set());
  const [editing, setEditing] = useState(null); // job object being edited

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/hero/all").then((r) => {
      setHeroSlides(r.data || []);
      if (r.data?.length) setHeroTarget(r.data[0].heading);
    }).catch(() => {});
  }, [isAdmin]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const j of jobs) {
      const k = keyFn[groupBy](j);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(j);
    }
    const entries = Array.from(map.entries());
    entries.sort((a, b) => (groupBy === "date" ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0])));
    return entries;
  }, [jobs, groupBy]);

  const setAsHero = async () => {
    if (!selected || !heroTarget) return;
    const slide = heroSlides.find((s) => s.heading === heroTarget);
    if (!slide) return;
    setBusy(true);
    try {
      await api.patch(`/hero/${slide.id}`, { image_url: jobPhotoSrc(selected), enabled: true });
      setMsg({ kind: "ok", text: `Photo is now the hero for "${heroTarget}" (and that section is turned on).` });
    } catch (e) {
      setMsg({ kind: "error", text: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const setPhase = async (phase) => {
    if (!selected) return;
    try {
      await api.patch(`/jobs/${selected.id}`, { phase });
      setSelected({ ...selected, phase: phase || null });
      onRefresh();
    } catch (e) { alert(formatApiError(e)); }
  };

  const removePhoto = async () => {
    if (!selected) return;
    if (!window.confirm(`Remove "${selected.job_site_name}" from the site? This hides it everywhere.`)) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${selected.id}`);
      setSelected(null);
      setMsg({ kind: "ok", text: "Photo removed from the site." });
      onRefresh();
    } catch (e) {
      setMsg({ kind: "error", text: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const toggleBatch = (id) => {
    setBatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const batchDelete = async () => {
    if (!batchIds.size) return;
    if (!window.confirm(`Delete ${batchIds.size} photo${batchIds.size === 1 ? "" : "s"} from the site? This hides them everywhere.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/jobs/bulk-delete", { ids: Array.from(batchIds) });
      setMsg({ kind: "ok", text: `${r.data?.deleted ?? batchIds.size} photos removed from the site.` });
      setBatchIds(new Set());
      setBatchMode(false);
      onRefresh();
    } catch (e) {
      setMsg({ kind: "error", text: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const onTileClick = (j) => {
    setMsg(null);
    if (batchMode) { toggleBatch(j.id); return; }
    setSelected(selected?.id === j.id ? null : j);
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-5 md:p-10" data-testid="photos-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#2C3539]">All Site Photos</div>
          <p className="text-sm text-[#2C3539]/70 mt-1">
            {isAdmin
              ? "Every photo on the public site. Tap a photo to set it as a hero, edit its details, tag its phase, or remove it."
              : "Every photo on the public site. Tap a photo to tag its phase (before / during / after)."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin && (
            <button
              onClick={() => { setBatchMode(!batchMode); setBatchIds(new Set()); setSelected(null); setMsg(null); }}
              data-testid="photos-batch-toggle"
              className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] px-4 py-2.5 border-2 transition-colors ${batchMode ? "bg-[#2F5F80] border-[#2F5F80] text-white" : "border-[#2C3539]/25 text-[#2C3539] hover:border-[#2F5F80] hover:text-[#2F5F80]"}`}
            >
              {batchMode ? <CheckSquare size={14} /> : <Square size={14} />} {batchMode ? "Done Selecting" : "Select Multiple"}
            </button>
          )}
          <label className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[#2C3539]/70">
            Organize by
            <select data-testid="photos-group-by" value={groupBy} onChange={(e) => { setGroupBy(e.target.value); }} className="border-2 border-[#2C3539]/20 focus:border-[#2F5F80] focus:outline-none px-3 py-2 text-sm bg-white text-[#2C3539]">
              {GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      {/* Batch action bar */}
      {batchMode && (
        <div className="mt-5 border-2 border-[#2F5F80] bg-[#2F5F80]/5 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="photos-batch-bar">
          <span className="text-sm font-semibold text-[#2C3539]" data-testid="photos-batch-count">{batchIds.size} selected</span>
          <button
            onClick={batchDelete}
            disabled={busy || batchIds.size === 0}
            data-testid="photos-batch-delete"
            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-[0.12em] px-4 py-2.5 transition-colors disabled:opacity-40"
          >
            <Trash2 size={13} /> Delete Selected
          </button>
          <span className="text-xs text-[#2C3539]/60">Tap photos to add or remove them from the selection.</span>
        </div>
      )}

      {/* Selected photo action panel */}
      {selected && !batchMode && (
        <PhotoActionPanel
          selected={selected}
          isAdmin={isAdmin}
          heroSlides={heroSlides}
          heroTarget={heroTarget}
          setHeroTarget={setHeroTarget}
          busy={busy}
          onSetHero={setAsHero}
          onSetPhase={setPhase}
          onEdit={() => setEditing(selected)}
          onRemove={removePhoto}
          onDeselect={() => setSelected(null)}
        />
      )}

      {msg && (
        <div data-testid={`photos-msg-${msg.kind}`} className={`mt-4 px-4 py-3 text-sm border-l-4 ${msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="mt-8 border-2 border-dashed border-[#2C3539]/20 p-10 text-center">
          <Camera size={32} className="mx-auto text-[#2F5F80]" />
          <div className="mt-3 font-display text-xl uppercase text-[#2C3539]">No photos yet</div>
          <p className="text-sm text-[#2C3539]/60">Use the upload panel above to add the first one.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-8" data-testid="photos-groups">
          {groups.map(([groupName, items]) => (
            <div key={groupName} data-testid={`photos-group-${groupName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <div className="flex items-center gap-3">
                <div className="font-display text-lg uppercase text-[#2C3539]">{groupName}</div>
                <div className="h-px flex-1 bg-[#2C3539]/10" />
                <span className="font-mono-av text-xs text-[#2C3539]/50">{items.length}</span>
              </div>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="photos-tiles">
                {items.map((j) => {
                  const isSel = batchMode ? batchIds.has(j.id) : selected?.id === j.id;
                  return (
                    <button
                      key={j.id}
                      onClick={() => onTileClick(j)}
                      data-testid={`photo-tile-${j.id}`}
                      title={`${j.job_site_name} — ${j.date}`}
                      className={`relative overflow-hidden border-2 transition-colors ${isSel ? "border-[#2F5F80]" : "border-[#2C3539]/15 hover:border-[#2C3539]/50"}`}
                    >
                      <div className="w-20 h-14 bg-[#2C3539]">
                        <img src={jobPhotoSrc(j)} alt={j.job_site_name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      {isSel && <span className="absolute inset-0 bg-[#2F5F80]/30 pointer-events-none" />}
                      {batchMode && isSel && (
                        <span className="absolute top-0.5 right-0.5 bg-[#2F5F80] text-white p-0.5 pointer-events-none"><CheckSquare size={10} /></span>
                      )}
                      {j.phase && (
                        <span className="absolute bottom-0 inset-x-0 bg-[#2F5F80]/90 text-white text-[8px] font-bold uppercase tracking-[0.15em] text-center py-0.5">{j.phase}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditPhotoModal
          job={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            setSelected((s) => (s && s.id === updated.id ? { ...s, ...updated } : s));
            setMsg({ kind: "ok", text: "Photo details updated." });
            onRefresh();
          }}
        />
      )}
    </section>
  );
}

const editInputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2.5 text-sm text-[#23282B]";

function PhotoActionPanel({ selected, isAdmin, heroSlides, heroTarget, setHeroTarget, busy, onSetHero, onSetPhase, onEdit, onRemove, onDeselect }) {
  return (
    <div className="mt-5 border-2 border-[#2F5F80] bg-[#F1F3F5] p-4" data-testid="photo-action-panel">
      <div className="flex flex-col sm:flex-row gap-4">
        <img src={jobPhotoSrc(selected)} alt={selected.job_site_name} className="w-full sm:w-36 h-40 sm:h-28 object-cover shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl uppercase text-[#2C3539]">{selected.job_site_name}</div>
          <div className="text-xs text-[#2C3539]/70 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span>{selected.service_type}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={11} /> {selected.location}</span>
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {selected.date}</span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {isAdmin && (
              <>
                <select data-testid="photo-hero-target" value={heroTarget} onChange={(e) => setHeroTarget(e.target.value)} className="border-2 border-[#2C3539]/20 focus:border-[#2F5F80] focus:outline-none px-2.5 py-2 text-xs bg-white text-[#2C3539] max-w-full">
                  {heroSlides.map((s) => <option key={s.id} value={s.heading}>{s.heading}</option>)}
                </select>
                <button onClick={onSetHero} disabled={busy} data-testid="photo-set-hero" className="inline-flex items-center gap-1.5 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold uppercase tracking-[0.12em] px-4 py-2.5 transition-colors disabled:opacity-50">
                  <Star size={13} /> Set as Hero
                </button>
              </>
            )}
            <select data-testid="photo-phase-select" value={selected.phase || ""} onChange={(e) => onSetPhase(e.target.value)} className="border-2 border-[#2C3539]/20 focus:border-[#2F5F80] focus:outline-none px-2.5 py-2 text-xs bg-white text-[#2C3539]">
              {PHASES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {isAdmin && (
              <>
                <button onClick={onEdit} data-testid="photo-edit" className="inline-flex items-center gap-1.5 border-2 border-[#2C3539]/25 text-[#2C3539] hover:border-[#2F5F80] hover:text-[#2F5F80] text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 transition-colors">
                  <Pencil size={13} /> Edit Details
                </button>
                <button onClick={onRemove} disabled={busy} data-testid="photo-remove" className="inline-flex items-center gap-1.5 border-2 border-red-500 text-red-600 hover:bg-red-600 hover:text-white text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 transition-colors disabled:opacity-50">
                  <Trash2 size={13} /> Remove
                </button>
              </>
            )}
            <button onClick={onDeselect} data-testid="photo-deselect" className="text-xs font-semibold text-[#2C3539]/60 hover:text-[#2C3539] underline underline-offset-2 ml-auto">
              Deselect
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditPhotoModal({ job, onClose, onSaved }) {
  const [form, setForm] = useState({
    job_site_name: job.job_site_name || "",
    location: job.location || "",
    date: job.date || "",
    service_type: job.service_type || "Excavation",
    description: job.description || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.patch(`/jobs/${job.id}`, form);
      onSaved({ id: job.id, ...form });
    } catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="photo-edit-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg p-7 border-t-4 border-[#2F5F80] my-auto max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl uppercase text-[#23282B]">Edit Photo Details</div>
          <button type="button" onClick={onClose} data-testid="photo-edit-close" className="p-1 text-[#23282B]/60 hover:text-[#23282B]"><X size={20} /></button>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Job Site Name *</span>
            <input data-testid="photo-edit-name" required value={form.job_site_name} onChange={set("job_site_name")} className={editInputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Location</span>
            <input data-testid="photo-edit-location" value={form.location} onChange={set("location")} className={editInputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Date</span>
            <input data-testid="photo-edit-date" type="date" value={form.date} onChange={set("date")} className={editInputCls} /></label>
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Service Type</span>
            <select data-testid="photo-edit-service" value={form.service_type} onChange={set("service_type")} className={editInputCls}>
              {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select></label>
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Notes</span>
            <textarea data-testid="photo-edit-description" rows={3} value={form.description} onChange={set("description")} className={editInputCls} /></label>
        </div>
        {error && <div data-testid="photo-edit-error" className="mt-4 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-700">{error}</div>}
        <button type="submit" disabled={busy} data-testid="photo-edit-save" className="mt-6 inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors disabled:opacity-50">
          {busy ? "Saving…" : "Save Changes"}
        </button>
      </form>
    </div>
  );
}
